import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { apiErrorText } from '../../../shared/api/errorText'
import { getProcurementResults } from '../../../shared/api/requestItems.api'
import type { ProcurementResult, SourceType } from '../../../shared/api/types'
import { toast } from '../../../shared/ui/toastStore'
import { useMatrixCellQuotes } from '../hooks/useMatrixCellQuotes'
import type { MatrixRates } from '../utils/convertQuoteToMatrixField'
import { useAuthStore } from '../../auth/authStore'
import { SupplierDuplicateHint } from '../../suppliers/components/SupplierDuplicateHint'

export interface MatrixQuotesTarget {
  itemId: string
  sourceType: SourceType
  matrixLabel: string
  /** Положение ячейки-якоря (getBoundingClientRect) — поповер встаёт под ней. */
  anchor: { top: number; left: number; bottom: number; right: number }
}

const CURRENCIES = ['USD', 'EUR', 'CNY', 'RUB']

/**
 * Поповер со списком квот одной матрицы для позиции. Открывается кликом по
 * бейджу-счётчику в ячейке. Показывает существующие квоты с инлайн-правкой и
 * удалением + кнопку «Добавить квоту» (открывает модалку создания через
 * onAddNew). Значение ячейки (последняя внесённая цена) пересчитывается в
 * useMatrixCellQuotes при любой правке/удалении.
 */
export function MatrixQuotesPopover({
  target,
  requestId,
  rates,
  readOnly = false,
  onAddNew,
  onClose,
}: {
  target: MatrixQuotesTarget
  requestId: string | undefined
  rates: MatrixRates | null | undefined
  /** true для ролей без права править цены (просмотр квот без кнопок). */
  readOnly?: boolean
  onAddNew: () => void
  onClose: () => void
}) {
  const { updateQuote, deleteQuote, ratesReady } = useMatrixCellQuotes(requestId, rates)
  // Без курсов создание и правка квоты заблокированы: значение ячейки после них
  // пересчитывается, а считать не по чему — в позицию уехала бы выдуманная цена.
  const canWrite = !readOnly && ratesReady
  const { user } = useAuthStore()
  const { data: allQuotes, isLoading } = useQuery({
    queryKey: ['procurement-results', target.itemId],
    queryFn: () => getProcurementResults(target.itemId),
  })
  const quotes = (allQuotes ?? []).filter((q) => q.sourceType === target.sourceType)

  // Карандаш «Изменить» — только на своих квотах (бэк в updateResult пускает
  // лишь автора и ADMIN; чужой карандаш кончался toast-ошибкой 403).
  const canEditQuote = (q: ProcurementResult) =>
    user?.role === 'ADMIN' || (q.creator?.id != null && q.creator.id === user?.id)
  // Удалить чужую квоту менеджер/РОП может (давнее согласованное правило),
  // а вот ASSISTANT (Помощник) — только свою: крестик на чужих ему не показываем.
  const canDeleteQuote = (q: ProcurementResult) =>
    user?.role === 'ASSISTANT' ? canEditQuote(q) : true
  // Удалению курсы нужны только тогда, когда в матрице остаются другие квоты
  // с ценой: ячейка откатывается на их цену. Если удаляется последняя — поле
  // просто обнуляется, и запрещать удаление из-за курсов незачем.
  const canDeleteNow = (q: ProcurementResult) =>
    !readOnly &&
    canDeleteQuote(q) &&
    (ratesReady || quotes.every((other) => other.id === q.id || other.price == null))

  const [editingId, setEditingId] = useState<string | null>(null)
  const [editForm, setEditForm] = useState<{
    supplierName: string
    price: string
    currency: string
    note: string
  } | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)

  const startEdit = (q: ProcurementResult) => {
    setEditingId(q.id)
    setEditForm({
      supplierName: q.supplierName,
      price: q.price == null ? '' : String(q.price),
      currency: q.currency ?? 'CNY',
      note: q.note ?? '',
    })
  }

  // Раньше у правки и удаления не было catch (№309): отказ сервера уходил в
  // необработанный reject, кнопка возвращалась в исходное состояние — и было
  // непонятно, сохранилось ли что-нибудь. Квоты и позицию после сбоя
  // перезапрашивает useMatrixCellQuotes.
  const saveEdit = async (q: ProcurementResult) => {
    if (!editForm) return
    setBusyId(q.id)
    try {
      await updateQuote(q.id, target.itemId, target.sourceType, {
        supplierName: editForm.supplierName.trim(),
        price: parseFloat(editForm.price),
        currency: editForm.currency,
        note: editForm.note.trim() || null,
      })
      setEditingId(null)
      setEditForm(null)
    } catch (err) {
      toast.error(apiErrorText(err, 'Не удалось сохранить квоту'))
    } finally {
      setBusyId(null)
    }
  }

  const doDelete = async (q: ProcurementResult) => {
    setBusyId(q.id)
    try {
      await deleteQuote(q.id, target.itemId, target.sourceType, quotes)
      setConfirmDeleteId(null)
    } catch (err) {
      toast.error(apiErrorText(err, 'Не удалось удалить квоту'))
    } finally {
      setBusyId(null)
    }
  }

  // Поповер ставим под ячейкой, выравниваем по правому краю (цены справа),
  // с clamp по краям окна, чтобы не уезжал за экран.
  const width = 340
  const left = Math.max(8, Math.min(target.anchor.right - width, window.innerWidth - width - 8))
  const top = Math.min(target.anchor.bottom + 4, window.innerHeight - 280)

  return (
    <div className="fixed inset-0 z-50" onClick={onClose}>
      <div
        className="absolute bg-white rounded-xl shadow-xl border border-gray-200 text-xs"
        style={{ top, left, width }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-3 py-2 border-b border-gray-100 flex items-center justify-between">
          <span className="font-medium text-gray-700">Квоты · матрица «{target.matrixLabel}»</span>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 text-base leading-none"
          >
            ×
          </button>
        </div>

        <div className="max-h-64 overflow-y-auto divide-y divide-gray-50">
          {isLoading ? (
            <p className="px-3 py-3 text-gray-400">Загрузка…</p>
          ) : quotes.length === 0 ? (
            <p className="px-3 py-3 text-gray-400">Квот по этой матрице нет</p>
          ) : (
            quotes.map((q) =>
              editingId === q.id && editForm ? (
                <div key={q.id} className="p-2 bg-amber-50/50 space-y-1.5">
                  <input
                    value={editForm.supplierName}
                    onChange={(e) => setEditForm({ ...editForm, supplierName: e.target.value })}
                    placeholder="Поставщик"
                    className="w-full px-2 py-1 border border-gray-300 rounded"
                  />
                  <SupplierDuplicateHint
                    name={editForm.supplierName}
                    onPick={(n) => setEditForm({ ...editForm, supplierName: n })}
                  />
                  <div className="flex gap-1">
                    <input
                      type="number"
                      value={editForm.price}
                      onChange={(e) => setEditForm({ ...editForm, price: e.target.value })}
                      placeholder="Цена"
                      className="flex-1 px-2 py-1 border border-gray-300 rounded"
                    />
                    <select
                      value={editForm.currency}
                      onChange={(e) => setEditForm({ ...editForm, currency: e.target.value })}
                      disabled={target.sourceType === 'RF'}
                      className="w-16 px-1 py-1 border border-gray-300 rounded bg-white disabled:bg-gray-100"
                    >
                      {CURRENCIES.map((c) => (
                        <option key={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                  <input
                    value={editForm.note}
                    onChange={(e) =>
                      setEditForm({ ...editForm, note: e.target.value.slice(0, 500) })
                    }
                    placeholder="Заметка"
                    className="w-full px-2 py-1 border border-gray-300 rounded"
                  />
                  <div className="flex gap-1 justify-end">
                    <button
                      onClick={() => void saveEdit(q)}
                      disabled={
                        busyId === q.id ||
                        !editForm.supplierName.trim() ||
                        !(parseFloat(editForm.price) > 0)
                      }
                      className="px-2 py-1 bg-brand-600 text-white rounded hover:bg-brand-700 disabled:opacity-50"
                    >
                      {busyId === q.id ? '…' : 'OK'}
                    </button>
                    <button
                      onClick={() => {
                        setEditingId(null)
                        setEditForm(null)
                      }}
                      className="px-2 py-1 text-gray-500 hover:text-gray-900"
                    >
                      ×
                    </button>
                  </div>
                </div>
              ) : (
                <div key={q.id} className="px-3 py-2 flex items-start gap-2 hover:bg-gray-50/50">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900 truncate">{q.supplierName}</div>
                    {q.note && <div className="text-gray-500 truncate">{q.note}</div>}
                  </div>
                  <div className="text-right font-mono text-gray-900 whitespace-nowrap">
                    {q.price == null ? '—' : `${q.price} ${q.currency ?? ''}`}
                  </div>
                  {confirmDeleteId === q.id && canDeleteNow(q) ? (
                    <div className="flex gap-1 items-center">
                      <button
                        onClick={() => void doDelete(q)}
                        disabled={busyId === q.id}
                        className="px-1.5 py-0.5 text-white bg-red-600 rounded hover:bg-red-700 disabled:opacity-50"
                      >
                        {busyId === q.id ? '…' : 'Удалить'}
                      </button>
                      <button
                        onClick={() => setConfirmDeleteId(null)}
                        className="px-1 text-gray-500 hover:text-gray-900"
                      >
                        ×
                      </button>
                    </div>
                  ) : (
                    <div className="flex gap-0.5">
                      {canWrite && canEditQuote(q) && (
                        <button
                          onClick={() => startEdit(q)}
                          className="text-gray-400 hover:text-brand-700 px-1"
                          title="Изменить"
                        >
                          ✎
                        </button>
                      )}
                      {canDeleteNow(q) && (
                        <button
                          onClick={() => setConfirmDeleteId(q.id)}
                          className="text-gray-400 hover:text-red-600 px-1"
                          title="Удалить"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ),
            )
          )}
        </div>

        {!readOnly && !ratesReady && (
          <p className="px-3 py-2 border-t border-gray-100 text-red-700 bg-red-50">
            Курсы валют не загрузились — править квоты нельзя.
          </p>
        )}

        {canWrite && (
          <div className="px-3 py-2 border-t border-gray-100">
            <button onClick={onAddNew} className="text-brand-600 hover:text-brand-700 font-medium">
              + Добавить квоту
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
